import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { reportsService } from "../../../api";
import { Table, Button, BackArrow } from "../../../components";
import { useUi } from "../../../context/ui.context";
import { usePageTitle } from "../../../context/navigationContext";

interface FlavorReport {
  base_revenue: number;
  base_revenue_currency: string;
  extras_revenue: number;
  total_revenue: number;
  instance_count: number;
}

interface RegionReport {
  base_revenue: number;
  base_revenue_currency: string;
  extras_revenue: number;
  total_revenue: number;
  instance_count: number;
  per_flavor_report: {
    [flavorName: string]: FlavorReport;
  };
  flavor_names: string[];
}

interface EstimatedInstanceRevenueReport {
  per_region_report: {
    [region: string]: RegionReport;
  };
  total_instances: number;
  total_base_revenue: number;
  total_extras_revenue: number;
  total_revenue: number;
  revenue_currency: string;
}

const EstimatedInstanceRevenue: React.FC = () => {
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("ESTIMATED_INSTANCE_REVENUE_TITLE"));
  const [report, setReport] = useState<EstimatedInstanceRevenueReport | null>(null);
  const [regionNames, setRegionNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRegionName, setLoadingRegionName] = useState<string | null>(null);
  const [loadingRegionCount, setLoadingRegionCount] = useState(0);
  const [hideZeroInstancesFlavors, setHideZeroInstancesFlavors] = useState(true);
  const [regionReports, setRegionReports] = useState<any>({});

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);
      const regions = ["default"];
      setRegionNames(regions);

      if (regions.length === 0) {
        viewAlert({
          severity: "info",
          message: t("NO_REGIONS_ERROR"),
        });
        navigate("/utilities/reports");
        return;
      }

      await retrieveRegionReport(regions[0], regions);
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_LOAD_REPORT"),
      });
      navigate("/utilities/reports");
    }
  };

  const retrieveRegionReport = async (
    regionName: string,
    allRegions: string[],
    currentReports: any = {}
  ) => {
    try {
      setLoadingRegionName(regionName);
      setLoadingRegionCount((prev) => prev + 1);

      const response = await reportsService.getReportForRegion(regionName);
      const updatedReports = {
        ...currentReports,
        [regionName]: response.data.report,
      };
      setRegionReports(updatedReports);

      if (Object.keys(updatedReports).length < allRegions.length) {
        const nextRegion = allRegions.find(
          (region) => !Object.keys(updatedReports).includes(region)
        );
        if (nextRegion) {
          await retrieveRegionReport(nextRegion, allRegions, updatedReports);
        }
      } else {
        await combineRegionReports(updatedReports);
      }
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_PROCESS_REGION"),
      });
      navigate("/utilities/reports");
    }
  };

  const combineRegionReports = async (reports: any) => {
    try {
      const response = await reportsService.combineRegionReports(
        JSON.stringify(reports)
      );
      setReport(response.data.report);
      setRegionNames(Object.keys(response.data.report.per_region_report));
      setLoading(false);
    } catch (error: any) {
      viewAlert({
        severity: "error",
        message: error?.response?.data?.detail || t("FAILED_COMBINE_REPORTS"),
      });
      navigate("/utilities/reports");
    }
  };

  const formatCurrency = (value: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  };

  const exportToCSV = () => {
    if (!report) return;

    const table = document.getElementById("report-table");
    if (!table) return;

    const rows = table.querySelectorAll("tr");
    const data: string[] = [];

    rows.forEach((row) => {
      const cols = row.querySelectorAll("td, th");
      const rowData: string[] = [];
      cols.forEach((col) => {
        rowData.push(`"${col.textContent || ""}"`);
      });
      data.push(rowData.join(","));
    });

    const csv = data.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.download = "estimated_instance_revenue_report.csv";
    link.href = window.URL.createObjectURL(blob);
    link.click();
  };

  const printReport = () => {
    window.print();
  };

  // Prepare table data
  const tableRows = report
    ? [
        ...regionNames.flatMap((regionName) => {
          const regionData = report.per_region_report[regionName];
          const regionRow = {
            id: `region-${regionName}`,
            name: regionName,
            instance_count: regionData.instance_count,
            base_revenue: formatCurrency(
              regionData.base_revenue,
              regionData.base_revenue_currency
            ),
            extras_revenue: formatCurrency(
              regionData.extras_revenue,
              regionData.base_revenue_currency
            ),
            total_revenue: formatCurrency(
              regionData.total_revenue,
              regionData.base_revenue_currency
            ),
            isRegion: true,
          };

          const flavorRows = regionData.flavor_names
            ?.filter((flavorName) => {
              const flavorReport = regionData.per_flavor_report[flavorName];
              return !hideZeroInstancesFlavors || flavorReport.instance_count !== 0;
            })
            .map((flavorName) => {
              const flavorReport = regionData.per_flavor_report[flavorName];
              return {
                id: `flavor-${regionName}-${flavorName}`,
                name: flavorName,
                instance_count: flavorReport.instance_count,
                base_revenue: formatCurrency(
                  flavorReport.base_revenue,
                  flavorReport.base_revenue_currency
                ),
                extras_revenue: formatCurrency(
                  flavorReport.extras_revenue,
                  flavorReport.base_revenue_currency
                ),
                total_revenue: formatCurrency(
                  flavorReport.total_revenue,
                  flavorReport.base_revenue_currency
                ),
                isFlavor: true,
              };
            });

          return [regionRow, ...(flavorRows || [])];
        }),
        {
          id: "total",
          name: t("TOTAL"),
          instance_count: report.total_instances,
          base_revenue: formatCurrency(
            report.total_base_revenue,
            report.revenue_currency
          ),
          extras_revenue: formatCurrency(
            report.total_extras_revenue,
            report.revenue_currency
          ),
          total_revenue: formatCurrency(
            report.total_revenue,
            report.revenue_currency
          ),
          isTotal: true,
        },
      ]
    : [];

  const columns = [
    { title: "", key: "name" },
    { title: t("INSTANCE_COUNT"), key: "instance_count" },
    { title: t("BASE_REVENUE"), key: "base_revenue" },
    { title: t("EXTRAS_REVENUE"), key: "extras_revenue" },
    { title: t("TOTAL_REVENUE"), key: "total_revenue" },
  ];

  return (
    <Container>
      <BackArrow fallbackPath="/utilities/reports" />
      <Title>{t("ESTIMATED_INSTANCE_REVENUE_TITLE")}</Title>
      <Description>{t("ESTIMATED_INSTANCE_INFO")}</Description>

      {loading ? (
        <>
          <Skeleton height={40} width={250} style={{ marginBottom: 20 }} />
          <Skeleton height={400} style={{ marginBottom: 20 }} />
          <LoadingText>
            {t("REPORT_GENERATION_MESSAGE")}
            {loadingRegionName && (
              <div>
                {t("PROCESSING_REGION")} <strong>{loadingRegionName}</strong> (
                {loadingRegionCount} {t("OF_REGIONS", { count: regionNames.length })}
                ) ...
              </div>
            )}
          </LoadingText>
        </>
      ) : (
        report && (
          <>
            <CheckboxContainer>
              <input
                type="checkbox"
                id="hideZero"
                checked={hideZeroInstancesFlavors}
                onChange={(e) => setHideZeroInstancesFlavors(e.target.checked)}
              />
              <label htmlFor="hideZero">{t("HIDE_ZERO_FLAVORS")}</label>
            </CheckboxContainer>

            <TableWrapper id="report-table">
              <Table
                columns={columns}
                rows={tableRows}
                hideCheckbox
                hideOther
              />
            </TableWrapper>

            <ButtonContainer>
              <Button
                variant="stroke"
                size="display"
                title={t("GET_PDF")}
                onClick={printReport}
              />
              <Button
                variant="stroke"
                size="display"
                title={t("EXPORT_TO_CSV")}
                onClick={exportToCSV}
              />
            </ButtonContainer>
          </>
        )
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
  margin-bottom: 16px;
  margin-top: 16px;
`;

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.palette[700]};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.palette[700]};
  text-align: center;
  line-height: 1.6;
  margin-top: 20px;

  strong {
    color: ${({ theme }) => theme.palette[900]};
  }
`;

const CheckboxContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="checkbox"] {
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${({ theme }) => theme.palette[900]};
    cursor: pointer;
  }
`;

const TableWrapper = styled.div`
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

export default EstimatedInstanceRevenue;
